import React, { useState, useEffect } from 'react';
import rbacApi from '../../api/rbacApi';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Search,
  FilterList,
  Person,
  AdminPanelSettings,
  Group
} from '@mui/icons-material';
import { dsVar, ink } from '../../theme/tokens';

// No mock data - using real API data only

interface UsersManagementProps {
  onSwitchToInvitations?: () => void;
  canManageUsers?: boolean;
  canDeleteUsers?: boolean;
}

const UsersManagement: React.FC<UsersManagementProps> = ({ 
  onSwitchToInvitations,
  canManageUsers = true,
  canDeleteUsers = true
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [promoting, setPromoting] = useState<string | null>(null);

  // Permission check functions
  const isOrgAdmin = () => userRole === 'organisation_admin';

  // Load current user info
  const loadUserInfo = async () => {
    try {
      const email = localStorage.getItem('user_email') || '';
      setUserEmail(email);
      
      const response = await rbacApi.getCurrentUser(email || null as any);
      
      if (response && response.user && response.user.role) {
        setUserRole(response.user.role);
      } else if (response && response.role) {
        setUserRole(response.role);
      } else if (response && response.userRole) {
        setUserRole(response.userRole);
      } else {
        const role = localStorage.getItem('user_role') || '';
        setUserRole(role);
      }
    } catch (error) {
      console.error('Error loading user info:', error);
      const email = localStorage.getItem('user_email') || '';
      const role = localStorage.getItem('user_role') || '';
      setUserEmail(email);
      setUserRole(role);
    }
  };

  // Promote user to org admin
  const handlePromoteToOrgAdmin = async (user: any) => {
    try {
      setPromoting(user.id);
      console.log('Promoting user to org admin:', user);
      
      // Call API to update user role to organisation_admin
      await rbacApi.updateUser(user.id, {
        role: 'organisation_admin',
        updatedBy: userEmail
      });
      
      // Refresh the users list to show updated role
      await loadUsers();
      
      alert(`Successfully promoted ${user.name} to Organisation Admin`);
    } catch (error) {
      console.error('Error promoting user:', error);
      alert(`Failed to promote ${user.name}. Please try again.`);
    } finally {
      setPromoting(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'organisation_admin':
        return 'error';
      case 'user_group_admin':
        return 'primary';
      case 'user':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'organisation_admin':
        return <AdminPanelSettings />;
      case 'user_group_admin':
        return <Group />;
      case 'user':
        return <Person />;
      default:
        return <Person />;
    }
  };

  // Load users from backend
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await rbacApi.getUsers();
      
      if (response && Array.isArray(response)) {
        setUsers(response);
      } else if (response && response.users) {
        const mappedUsers = response.users.map((user: any) => {
          const userId = user._id || user.id || user.userId || user.user_id;
          const userName = user.userName || user.name || user.fullName || user.displayName || user.username;
          const userEmail = user.emailId || user.email || user.emailAddress || user.email_address;
          const userRole = user.role || user.userRole || user.user_role || user.permission;
          const userStatus = user.status || user.userStatus || user.user_status || user.active;
          const userStartDate = user.startDate || user.createdAt || user.created_at || user.joinDate;
          const userEndDate = user.endDate || user.terminatedAt || user.terminated_at;
          
          // Dynamic role mapping
          let mappedRole = 'user';
          if (userRole) {
            const role = userRole.toString().toUpperCase();
            if (['ADMIN', 'ORGANISATION_ADMIN', 'ORG_ADMIN'].includes(role)) {
              mappedRole = 'organisation_admin';
            } else if (['USER_GROUP_ADMIN', 'GROUP_ADMIN', 'TEAM_ADMIN'].includes(role)) {
              mappedRole = 'user_group_admin';
            }
          }
          
          // Dynamic status mapping
          let mappedStatus = 'inactive';
          if (userStatus) {
            const status = userStatus.toString().toUpperCase();
            if (['ACTIVE', 'ENABLED'].includes(status)) {
              mappedStatus = 'active';
            }
          }
          
          return {
            id: userId,
            name: userName || 'Unknown User',
            email: userEmail || 'no-email@example.com',
            role: mappedRole,
            status: mappedStatus,
            organisationId: localStorage.getItem('org_id') || 'fixmyclaim',
            startDate: userStartDate,
            endDate: userEndDate
          };
        });
        setUsers(mappedUsers);
      } else {
        setUsers([]);
        setError('No users found.');
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users from API.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Load users on component mount and when filters change
  useEffect(() => {
    loadUserInfo();
    loadUsers();
  }, [searchTerm, filterRole]);

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  return (
    <Box>
      {/* Users Table */}
      <Card sx={{ 
        bgcolor: dsVar('surface'), 
        borderRadius: '16px', 
        border: `1px solid ${dsVar('line')}`,
        boxShadow: 'none',
        overflow: 'hidden'
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Typography variant="h6" sx={{ color: dsVar('fg'), fontWeight: 700 }}>
              Organization Users
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                placeholder="Search users..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: dsVar('page'),
                    color: dsVar('fg'),
                    borderRadius: '8px',
                    '& fieldset': { borderColor: dsVar('line') },
                    '&:hover fieldset': { borderColor: dsVar('accent-fg') },
                    '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                  },
                  '& .MuiInputBase-input::placeholder': { color: dsVar('fg-2'), opacity: 1 }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: dsVar('fg-2') }} />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                sx={{
                  color: dsVar('fg-2'),
                  borderColor: dsVar('line'),
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: dsVar('accent-fg'),
                    color: dsVar('accent-fg'),
                    bgcolor: 'rgba(255, 222, 89, 0.05)'
                  }
                }}
              >
                Filter
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none', border: `1px solid ${dsVar('line')}`, borderRadius: '12px', overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: dsVar('page') }}>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 600, borderBottom: `1px solid ${dsVar('line')}` }}>User</TableCell>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 600, borderBottom: `1px solid ${dsVar('line')}` }}>Role</TableCell>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 600, borderBottom: `1px solid ${dsVar('line')}` }}>Status</TableCell>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 600, borderBottom: `1px solid ${dsVar('line')}` }}>Last Active</TableCell>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 600, borderBottom: `1px solid ${dsVar('line')}` }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8, border: 'none' }}>
                      <CircularProgress sx={{ color: dsVar('accent-fg') }} />
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8, color: dsVar('fg-2'), border: 'none' }}>
                      No users found matching your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} sx={{ '&:hover': { bgcolor: 'rgba(255, 222, 89, 0.02)' }, '& td': { borderBottom: `1px solid ${dsVar('line')}` } }}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ 
                            width: 36, 
                            height: 36, 
                            bgcolor: dsVar('line'), 
                            color: dsVar('accent-fg'), 
                            fontSize: '0.875rem', 
                            fontWeight: 700,
                            border: '1px solid rgba(255, 222, 89, 0.2)'
                          }}>
                            {user.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ color: dsVar('fg'), fontWeight: 600 }}>
                              {user.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: dsVar('fg-2') }}>
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getRoleIcon(user.role)}
                          label={user.role.replace('_', ' ').toUpperCase()}
                          size="small"
                          sx={{
                            bgcolor: user.role === 'organisation_admin' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 222, 89, 0.1)',
                            color: user.role === 'organisation_admin' ? ink('#ef4444') : dsVar('accent-fg'),
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            borderRadius: '4px',
                            border: `1px solid ${user.role === 'organisation_admin' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255, 222, 89, 0.2)'}`,
                            '& .MuiChip-icon': { color: 'inherit', fontSize: '1rem' }
                          }}
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box sx={{ 
                            width: 6, 
                            height: 6, 
                            borderRadius: '50%', 
                            bgcolor: user.status === 'active' ? '#10b981' : '#f59e0b' 
                          }} />
                          <Typography variant="caption" sx={{ color: dsVar('fg'), textTransform: 'capitalize' }}>
                            {user.status}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: dsVar('fg') }}>
                          {user.startDate ? new Date(user.startDate).toLocaleDateString() : 'N/A'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: dsVar('fg-2') }}>
                          {user.startDate ? new Date(user.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={
                          !isOrgAdmin() 
                            ? "Only Organisation Admins can promote users" 
                            : user.role === 'organisation_admin' 
                              ? "User is already an Organisation Admin" 
                              : "Promote to Organisation Admin"
                        }>
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handlePromoteToOrgAdmin(user)}
                              disabled={!isOrgAdmin() || user.role === 'organisation_admin' || promoting === user.id}
                              sx={{
                                bgcolor: dsVar('accent'),
                                color: dsVar('on-accent'),
                                fontWeight: 700,
                                textTransform: 'none',
                                borderRadius: '6px',
                                '&:hover': {
                                  bgcolor: dsVar('accent'),
                                  boxShadow: '0 0 12px rgba(255, 222, 89, 0.3)'
                                },
                                '&.Mui-disabled': {
                                  bgcolor: dsVar('line'),
                                  color: dsVar('fg-2')
                                }
                              }}
                            >
                              {promoting === user.id ? 'Updating...' : 'Promote'}
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UsersManagement; 